import { useCallback, useEffect, useState } from 'react';
import { ActionIcon, Autocomplete, Button, Group, Stack, Text } from '@mantine/core';
import { IconPlus, IconSettings } from '@tabler/icons-react';
import type { DriveFolderActionComponentProps } from '@ayocore/platform/filemanager/client/registry/defineDriveActions';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import logger from '@ayocore/platform/common/helpers/logger';
import {
  applyInspectionPhotoTemplate,
  useInspectionRepository,
  usePhotoNameTemplateRepository,
} from '../../rpc';
import { PhotoNameTemplateFormModal } from './PhotoNameTemplateFormModal';
import { PhotoNameTemplatesModal } from './PhotoNameTemplatesModal';

/** Строгий разбор id инспекции из якоря папки (`mws:inspection:<id>`). */
function parseInspectionId(code: string | null | undefined): number | null {
  const match = code != null ? /^mws:inspection:(\d+)$/.exec(code) : null;
  return match ? Number(match[1]) : null;
}

/**
 * «Назвать фото по шаблону» из меню папки инспекции на Диске (план
 * drive_folder_lifecycle, этап 3): та же операция, что кнопка в галерее —
 * `applyInspectionPhotoTemplate` выбирает фото по entity-связи и категории,
 * поэтому одинаково переименовывает корень и все пользовательские подпапки.
 * Нумерация остаётся сквозной по инспекции (папка своей не заводит).
 *
 * Шаблоны фраз — паритет с галереей: кнопки быстрой подстановки по виду работ,
 * «＋ Фраза» (PhotoNameTemplateFormModal) и менеджер (PhotoNameTemplatesModal).
 *
 * Только СОДЕРЖИМОЕ модалки: обёртку `<Modal>` (заголовок = title действия,
 * z-index поверх дравера инспекции) открывает платформа через useModal —
 * см. host в FolderActionsMenu.
 *
 * Доступность действия дальше UI не считается — право проверяет сервер
 * (двухуровневый guard в самой функции).
 */
export function InspectionPhotoNamingModal({
  folder,
  scopeAnchor,
  refresh,
  close,
}: DriveFolderActionComponentProps) {
  const inspectionId = parseInspectionId(scopeAnchor?.code ?? folder.code);
  const inspectionRepo = useInspectionRepository();
  const templateRepo = usePhotoNameTemplateRepository();
  // Добавление/менеджер шаблонов — те же модалки, что в галерее (стек useModal).
  const tplFormModal = useModal(PhotoNameTemplateFormModal);
  const tplManagerModal = useModal(PhotoNameTemplatesModal);

  const [phrase, setPhrase] = useState('');
  const [workTypeId, setWorkTypeId] = useState<number | null>(null);
  const [templates, setTemplates] = useState<Array<{ id: number; label: string; value: string }>>(
    []
  );
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renamed, setRenamed] = useState<number | null>(null);

  // Вид работ инспекции — источник набора шаблонов (как в галерее).
  useEffect(() => {
    if (inspectionId == null) return;
    void (async () => {
      try {
        const inspection = await inspectionRepo.findById(inspectionId);
        setWorkTypeId(inspection?.work_type_id ?? null);
      } catch (err) {
        logger.error('[InspectionPhotoNamingModal] инспекция не загрузилась:', err);
      }
    })();
    // Прокси репозиториев пересоздаются на каждый рендер — в зависимостях только id.
  }, [inspectionId]);

  // Шаблоны фраз по виду работ; перечитываются после добавления/правки в менеджере.
  const reloadTemplates = useCallback(async () => {
    if (workTypeId == null) return;
    try {
      const list = await templateRepo.filterByWorkType(workTypeId).orderBy('id', 'asc').fetch();
      setTemplates((list ?? []).map(t => ({ id: t.id, label: t.label, value: t.value })));
    } catch (err) {
      logger.error('[InspectionPhotoNamingModal] шаблоны фраз не загрузились:', err);
    }
    // Прокси репозиториев пересоздаются на каждый рендер — в зависимостях только id.
  }, [workTypeId]);

  useEffect(() => {
    void reloadTemplates();
  }, [reloadTemplates]);

  const openAddTemplate = () => {
    if (workTypeId == null) return;
    tplFormModal.open(
      {
        workTypeId,
        onSuccess: () => {
          tplFormModal.close();
          void reloadTemplates();
        },
        title: 'Новый шаблон',
      },
      { size: 'md' }
    );
  };

  const openTemplatesManager = () => {
    if (workTypeId == null) return;
    tplManagerModal.open(
      {
        workTypeId,
        onChanged: () => void reloadTemplates(),
        title: 'Шаблоны имени фото',
      },
      { size: 'lg' }
    );
  };

  const handleApply = async () => {
    if (inspectionId == null || !phrase.trim()) return;
    setApplying(true);
    setError(null);
    try {
      const result = await applyInspectionPhotoTemplate({ inspectionId, phrase: phrase.trim() });
      setRenamed(result.renamed);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось переименовать фото');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Stack gap='sm'>
      {inspectionId == null ? (
        <Text size='sm' c='red'>
          Папка не привязана к инспекции.
        </Text>
      ) : renamed != null ? (
        <>
          <Text size='sm'>Переименовано фотографий: {renamed}.</Text>
          <Group justify='flex-end'>
            <Button size='xs' onClick={close}>
              Готово
            </Button>
          </Group>
        </>
      ) : (
        <>
          <Text size='sm' c='dimmed'>
            Все фото инспекции (включая подпапки) получат имена по фразе, нумерация — сквозная 1…N в
            порядке загрузки. Текущие имена будут заменены.
          </Text>
          {/* Шаблоны по виду работ — кнопки быстрой подстановки, как в галерее;
              «＋ Фраза» и шестерёнка — те же модалки добавления/менеджера. */}
          <Group gap={4} align='center' wrap='wrap'>
            <Text size='xs' c='dimmed' w={64}>
              Шаблоны
            </Text>
            {templates.map(t => (
              <Button
                key={t.id}
                size='compact-xs'
                variant='light'
                title={t.value}
                onClick={() => setPhrase(t.value)}
              >
                {t.label}
              </Button>
            ))}
            <Button
              size='compact-xs'
              variant='default'
              leftSection={<IconPlus size={12} />}
              disabled={workTypeId == null}
              onClick={openAddTemplate}
            >
              Фраза
            </Button>
            <ActionIcon
              size='sm'
              variant='subtle'
              color='gray'
              disabled={workTypeId == null}
              aria-label='Управление шаблонами'
              onClick={openTemplatesManager}
            >
              <IconSettings size={16} />
            </ActionIcon>
          </Group>
          <Autocomplete
            label='Фраза'
            placeholder='Выберите шаблон выше или впишите свою'
            data={templates.map(t => t.value)}
            value={phrase}
            onChange={setPhrase}
            comboboxProps={{ withinPortal: true }}
          />
          {error && (
            <Text size='sm' c='red'>
              {error}
            </Text>
          )}
          <Group justify='flex-end'>
            <Button size='xs' variant='default' onClick={close}>
              Отмена
            </Button>
            <Button
              size='xs'
              loading={applying}
              disabled={!phrase.trim()}
              onClick={() => void handleApply()}
            >
              Переименовать
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
}
